/* =====================================================================
   RENDER — неоновый рендер через кэш glow-спрайтов.
   Спрайты рендерятся один раз в offscreen-canvas и переиспользуются
   через drawImage (быстро), вместо shadowBlur каждый кадр.
   ===================================================================== */

import { TAU } from './utils';

// '#rgb'/'#rrggbb' → [r,g,b]
function parseHex(hex: string): number[] {
  if (hex[0] !== '#') return [255, 255, 255];
  if (hex.length === 4) return [parseInt(hex[1], 16) * 17, parseInt(hex[2], 16) * 17, parseInt(hex[3], 16) * 17];
  return [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16)];
}
function rgba(c: number[], a: number): string { return 'rgba(' + c[0] + ',' + c[1] + ',' + c[2] + ',' + a + ')'; }
// горячее ядро: смешать цвет к бело-жёлтому жару (центр пламени)
function hotColor(c: number[]): number[] {
  return [Math.round(c[0] + (255 - c[0]) * 0.62), Math.round(c[1] + (240 - c[1]) * 0.62), Math.round(c[2] + (190 - c[2]) * 0.62)];
}
// Раскладка мягких блобов под архетип — НЕ контур, а «куда лить огонь». [x, y, sizeFactor].
// shard=язык вверх, block=плотная масса, streak=горизонтальная смазка, vortex=спираль, прочее=пламя.
function shapeBlobs(shape: string, r: number): number[][] {
  if (shape === 'shard')  return [[0, -r * 0.45, 0.72], [0, r * 0.35, 0.6], [r * 0.12, -r * 0.05, 0.45]];
  if (shape === 'block')  return [[0, 0, 1.0], [-r * 0.4, -r * 0.35, 0.62], [r * 0.42, r * 0.2, 0.62], [r * 0.32, -r * 0.42, 0.5], [-r * 0.36, r * 0.4, 0.55]];
  if (shape === 'streak') return [[-r * 0.95, 0, 0.6], [-r * 0.32, 0, 0.72], [r * 0.32, 0, 0.72], [r * 0.95, 0, 0.6]];
  if (shape === 'vortex') { const a: number[][] = []; for (let i = 0; i < 6; i++) { const ang = i * 1.25, rr = r * (0.12 + i * 0.14); a.push([Math.cos(ang) * rr, Math.sin(ang) * rr, 0.5]); } return a; }
  if (shape === 'tri')    return [[0, -r * 0.35, 0.78], [-r * 0.42, r * 0.4, 0.55], [r * 0.42, r * 0.4, 0.55]];
  if (shape === 'hex')    return [[0, 0, 1.0], [0, -r * 0.55, 0.5], [0, r * 0.55, 0.5], [-r * 0.55, 0, 0.5], [r * 0.55, 0, 0.5]];
  if (shape === 'diamond')return [[0, 0, 0.95], [0, -r * 0.6, 0.5], [r * 0.18, -r * 0.15, 0.42], [-r * 0.18, r * 0.18, 0.42]];
  return [[0, 0, 1.0], [r * 0.25, -r * 0.2, 0.45], [-r * 0.25, r * 0.2, 0.45]];   // circle/square → мягкий огненный шар
}
// строка → стабильный seed-флоат (для детерминированной пертурбации формы по color+shape)
function hashStr(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return ((h >>> 0) % 99991) / 99991 * 50 + 1;
}

export const Render: any = {
  spriteCache: new Map(),

  // Глоу-точка (радиальный градиент) — для частиц, снарядов, осколков
  glowDot(color: string, radius: number) {
    radius = Math.max(1, Math.round(radius));   // квантование: кэш не растёт от float-радиусов
    const key = 'dot|' + color + '|' + radius;
    let c = this.spriteCache.get(key);
    if (c) return c;
    const pad = radius * 3;
    const size = Math.ceil(pad * 2);
    c = document.createElement('canvas');
    c.width = c.height = size;
    const g = c.getContext('2d');
    const cx = size / 2;
    const grd = g.createRadialGradient(cx, cx, 0, cx, cx, pad);
    grd.addColorStop(0, color);
    grd.addColorStop(0.25, color);
    grd.addColorStop(1, 'rgba(0,0,0,0)');
    g.fillStyle = grd;
    g.beginPath();
    g.arc(cx, cx, pad, 0, TAU);
    g.fill();
    c._off = cx;
    this.spriteCache.set(key, c);
    return c;
  },

  // ОГНЕННАЯ МАССА (враги/игрок): мягкие перекрывающиеся glow-блобы, аддитивно — НЕТ обводки, НЕТ линий,
  // НЕТ чёткой фигуры (Дмитрий: «всё огненное, жидкостное, даже не фигуры»). Архетип читается раскладкой
  // блобов (shapeBlobs) + цветом/размером, не контуром. Кэш per (color,radius,shape) — перф как раньше.
  glowShape(color: string, radius: number, shape: string) {
    radius = Math.max(1, Math.round(radius));
    const key = 'shape|' + color + '|' + radius + '|' + shape;
    let c = this.spriteCache.get(key);
    if (c) return c;
    const pad = Math.ceil(radius * 2.6 + 18);
    const size = pad * 2;
    c = document.createElement('canvas');
    c.width = c.height = size;
    const g = c.getContext('2d');
    const cx = size / 2;
    g.translate(cx, cx);
    const seed = hashStr(color + '|' + shape);
    const nz = (i: number) => { const v = Math.sin(seed * 12.9898 + i * 78.233) * 43758.5453; return (v - Math.floor(v)) * 2 - 1; };
    const blobs = shapeBlobs(shape, radius);
    const rgb = parseHex(color), hot = hotColor(rgb);
    g.globalCompositeOperation = 'lighter';   // аддитивно: в перекрытиях — плотный жар, по краям мягко тает (нет кромки)
    // нижний слой — дымное тело пламени (мягкие массы цвета, с лёгким jitter позиции)
    for (let i = 0; i < blobs.length; i++) {
      const b = blobs[i];
      const x = b[0] + nz(i * 2) * radius * 0.12, y = b[1] + nz(i * 2 + 1) * radius * 0.12;
      const rr = b[2] * radius * 1.55;
      const grd = g.createRadialGradient(x, y, 0, x, y, rr);
      grd.addColorStop(0, rgba(rgb, 0.6)); grd.addColorStop(0.45, rgba(rgb, 0.32)); grd.addColorStop(1, rgba(rgb, 0));
      g.fillStyle = grd; g.beginPath(); g.arc(x, y, rr, 0, TAU); g.fill();
    }
    // верхний слой — горячие ядра (бело-жёлтый центр → цвет → прозрачно): «жар внутри», без линий
    for (let i = 0; i < blobs.length; i++) {
      const b = blobs[i];
      const rr = b[2] * radius * 0.85;
      const grd = g.createRadialGradient(b[0], b[1], 0, b[0], b[1], rr);
      grd.addColorStop(0, rgba(hot, 0.95)); grd.addColorStop(0.5, rgba(rgb, 0.5)); grd.addColorStop(1, rgba(rgb, 0));
      g.fillStyle = grd; g.beginPath(); g.arc(b[0], b[1], rr, 0, TAU); g.fill();
    }
    g.globalCompositeOperation = 'source-over';
    c._off = cx;
    this.spriteCache.set(key, c);
    return c;
  },

  // Нарисовать кэш-спрайт с центром в (x,y), опц. поворот/масштаб/альфа
  blit(ctx: any, sprite: any, x: number, y: number, rot = 0, scale = 1, alpha = 1) {
    const off = sprite._off;
    ctx.globalAlpha = alpha;
    if (rot === 0 && scale === 1) {
      ctx.drawImage(sprite, x - off, y - off);
    } else {
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(rot);
      ctx.scale(scale, scale);
      ctx.drawImage(sprite, -off, -off);
      ctx.restore();
    }
    ctx.globalAlpha = 1;
  },
};
